import { defineComponent as _defineComponent } from 'vue'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import {
  type ListaItens,
  type SeletorItem,
} from '@Lar21/imaginii-web-components/vue'

import {
  encontrarOpcaoDoCatalogo,
  preservarEstadoSemSelecaoVazia,
  prepararEstadoSomenteLeitura,
} from '@next/compartilhados/catalogos/apresentacao/estado-seletor-catalogo'

import {
  CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO,
  criarEstadoSeletorCatalogoRegraDeCalculo,
  criarPropriedadesPontotelSeletorRegraDeCalculo,
  extrairValorDoSeletorCatalogoRegraDeCalculo,
  filtrarOpcoesRegraDeCalculo,
} from './adaptador-catalogo-regra-de-calculo'
import { usarCatalogoRegraDeCalculo } from './usar-catalogo-regra-de-calculo'
import type {
  FiltrosCatalogoRegraDeCalculo,
  OpcaoRegraDeCalculo,
  SelecaoCatalogoRegraDeCalculo,
} from '../catalogo-regra-de-calculo.tipos'

type Props = Readonly<{
  modelValue: SelecaoCatalogoRegraDeCalculo
  selecaoMultipla?: boolean
  tamanho?: 'P' | 'M' | 'G'
  filtros?: FiltrosCatalogoRegraDeCalculo
  desabilitado?: boolean
  somenteLeitura?: boolean
  permiteSelecionarTodos?: boolean
  permiteLimpar?: boolean
  habilitarDuploClique?: boolean
  label?: string
  placeholder?: string
  mensagemErro?: string | null
  info?: string | null
  opcaoNula?: string
}>


export default /*@__PURE__*/_defineComponent({
  __name: 'CatalogoRegraDeCalculo',
  props: {
    modelValue: { type: [Object, Array, null], required: true },
    selecaoMultipla: { type: Boolean, required: false, default: false },
    tamanho: { type: String, required: false, default: 'M' },
    filtros: { type: Object, required: false },
    desabilitado: { type: Boolean, required: false, default: false },
    somenteLeitura: { type: Boolean, required: false, default: false },
    permiteSelecionarTodos: { type: Boolean, required: false, default: true },
    permiteLimpar: { type: Boolean, required: false, default: true },
    habilitarDuploClique: { type: Boolean, required: false, default: false },
    label: { type: String, required: false },
    placeholder: { type: String, required: false },
    mensagemErro: { type: [String, null], required: false, default: null },
    info: { type: [String, null], required: false, default: null },
    opcaoNula: { type: String, required: false }
  },
  emits: ["update:modelValue", "filtrando-itens", "duplo-clique", "update:selecionouTodos"],
  setup(__props: any, { expose: __expose, emit: __emit }) {
  __expose();

const props = __props

const emit = __emit

const { t } = useI18n()
const catalogo = usarCatalogoRegraDeCalculo(computed(() => !props.somenteLeitura))

const opcoesFiltradas = computed(() => filtrarOpcoesRegraDeCalculo(
  catalogo.itens.value,
  props.filtros,
))

// Objetos recebidos pela edição continuam visíveis enquanto a lista carrega.
// Filtros explícitos mantêm a restrição também para a seleção.
const opcoesSelecionadas = computed<readonly OpcaoRegraDeCalculo[]>(() => {
  if (props.modelValue === null) return []
  return Array.isArray(props.modelValue)
    ? props.modelValue as readonly OpcaoRegraDeCalculo[]
    : [props.modelValue as OpcaoRegraDeCalculo]
})
const opcoesParaSelecao = computed(() => {
  const ids = new Set(opcoesFiltradas.value.map(({ id }) => id))
  return [
    ...opcoesFiltradas.value,
    ...filtrarOpcoesRegraDeCalculo(opcoesSelecionadas.value, props.filtros)
      .filter(({ id }) => !ids.has(id)),
  ]
})
const estadoSeletor = computed(() => {
  const estado = criarEstadoSeletorCatalogoRegraDeCalculo(
    opcoesParaSelecao.value,
    props.modelValue,
    props.selecaoMultipla,
    props.opcaoNula,
  )
  const disponiveis = criarEstadoSeletorCatalogoRegraDeCalculo(
    opcoesFiltradas.value, null, props.selecaoMultipla, props.opcaoNula,
  )
  return prepararEstadoSomenteLeitura(
    { carregados: disponiveis.carregados, selecionados: estado.selecionados },
    props.somenteLeitura,
    props.modelValue === null ? undefined : props.modelValue,
  )
})

const erroExibido = computed(() => catalogo.erro.value
  ? t(`${CHAVE_I18N_CATALOGO_REGRA_DE_CALCULO}.erroBusca`)
  : props.mensagemErro)

const propriedadesSeletor = computed(() => (
  criarPropriedadesPontotelSeletorRegraDeCalculo({
    traduzir: t,
    ...(props.label !== undefined ? { label: props.label } : {}),
    ...(props.placeholder !== undefined
      ? { placeholder: props.placeholder }
      : {}),
    erro: erroExibido.value,
  })
))

const alterarSelecao = (estado: ListaItens): void => {
  emit(
    'update:modelValue',
    extrairValorDoSeletorCatalogoRegraDeCalculo(
      preservarEstadoSemSelecaoVazia(
        estado,
        estadoSeletor.value,
        props.permiteLimpar,
      ),
      opcoesParaSelecao.value,
      props.selecaoMultipla,
    ),
  )
}

const tratarDuploClique = (item: SeletorItem): void => {
  const opcao = encontrarOpcaoDoCatalogo(opcoesFiltradas.value, item)
  if (opcao) emit('duplo-clique', opcao)
}


const __returned__ = { props, emit, t, catalogo, opcoesFiltradas, opcoesSelecionadas, opcoesParaSelecao, estadoSeletor, erroExibido, propriedadesSeletor, alterarSelecao, tratarDuploClique }
Object.defineProperty(__returned__, '__isScriptSetup', { enumerable: false, value: true })
return __returned__
}

})